import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import clsx from 'clsx';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _debounce from 'lodash/debounce';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Drawer from '@mui/material/Drawer';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
// import Typography from '@mui/material/Typography';
// import Divider from '@mui/material/Divider';

import ViewWeekIcon from '@mui/icons-material/ViewWeek';
// import CachedIcon from '@mui/icons-material/Cached';
import PauseIcon from '@mui/icons-material/Pause';
import PlayIcon from '@mui/icons-material/PlayArrow';

import { DataGridPro, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-pro';

import OSAnomalyRulesService from '../../../data/os-anomaly-rules-service';
import OSAnomalyService from '../../../data/os-anomaly-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import { GetEpochDateFromQuick, IsFloat, ToFixed, RuleTitle } from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';
import useInterval from '../../../libraries/useinterval';

import HighlightText from '../../../components/highlightText';
import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';
import SearchBar from '../../../components/searchbar';
import Heatmap from '../../../components/charts/heatmap';
import PopupMenu from '../../../components/popupmenu';
import DateTimePicker from '../../../components/styled/datetimepicker';
import { QUICK_DAY, QUICK_HOUR, QUICK_WEEK } from '../components/filter-bar';
// import LabeledToolbar from '../../../components/styled/toolbar';
import IPAddress from '../../../components/ipaddress';

import AnomalyDetail from './anomaly-detail';
import ColumnSelector from './column-selector';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        position: 'relative',
    },
    stealRoom: {
        paddingLeft: 13,
        paddingRight: 13,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        minWidth: 60,
    },
    datePicker: {
        maxWidth: 165,
        minWidth: 165,
    },
    nowrap: {
    // display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    mapped: {
        verticalAlign: 'middle',
        paddingRight: 5,
    },
    select: {
        marginTop: 0,
        marginBottom: -8,
        width: '100%',
    },
    divider: {
        height: 40,
        marginTop: 10,
    },
    severityChip: {
        margin: '0 auto',
        color: '#000',
        borderRadius: 20,
        padding: 5,
        minWidth: 31,
        textAlign: 'center',
    },
}));

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
const HEATMAP_HEIGHT = MC2CHECK ? 10 : 110;

const QUICK_FILTERS = [
    { key: QUICK_WEEK, title: 'Last Week' },
    { key: QUICK_DAY, title: 'Last Day' },
    { key: QUICK_HOUR, title: 'Last Hour' },
];

const REFRESH_RATE = 30000

const DEFAULT_SORT = [{ field:'execution_end_time', sort:'desc' }];

const Anomalies = (props) => {
    const classes = useStyles();
    const theme = useTheme();

    const tableAPIRef = useGridApiRef();
    const toolbarRef = useRef(null);
    const { width, height } = useWindowSize();

    const { updateProfile, getProfile } = useContext(ProfileContext);

    const perms = Auth.permissions.get('Anomalies');

    const [init, setInit] = useState(false);

    // data
    const [data, setData] = useState([]);
    const [heatmapData, setHeatmapData] = useState([]);
    const [rules, setRules] = useState([]);
    const [selectedRule, setSelectedRule] = useState('all');
    const [quick, setQuick] = useState(QUICK_HOUR);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [search, setSearch] = useState('');
    const [range, setRange] = useState([
        new Date(Date.now() - 60 * 60 * 1000).valueOf(), // default to 1 hour ago
        new Date().valueOf()
    ]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [columns, setColumns] = useState([]);
    const [hideColumns, setHideColumns] = useState({});

    // page state
    const [tableHeight, setTableHeight] = useState(550);
    const [columnWidth, setColumnWidth] = useState({});
    const [columnsOrder, setColumnsOrder] = useState({});
    const [selection, setSelection] = useState();
    const [showColumnsSelector, setShowColumnsSelector] = useState(false);
    const [limit, setLimit] = useState(50);
    const [after, setAfter] = useState();
    const [page, setPage] = useState(1);
    const [paused, setPaused] = useState(false);
    const [bySeverity, setBySeverity] = useState(null);

    const abortController = new AbortController();

    useEffect(() => {
        OSAnomalyRulesService.listRules().then(res => {
            const r = _get(res, 'Detectors', []);
            setRules(r);
        });
    }, []);

    useEffect(() => {
        if (init) {
            if (page > 1) {
                setPaused(true);
                getData(true);
            } else if (!paused) {
                getData(paused);
            }
        }
    }, [page, paused, sort, range, bySeverity]);

    useInterval(() => {
        getData();
    }, (!paused) ? REFRESH_RATE : null);

    // initial page setup from stashed user profile
    useEffect(() => {
        const profile = getProfile('anomalies');
        setColumnWidth(profile.widths || {});

        // this is the only time we set this variable to avoid flickering
        const f = OSAnomalyService.fields;
        setColumnsOrder(profile.sort || Object.keys(f).map((x, i) => ({ [f[x].variable]: i })));
    }, [getProfile]);

    // columns order updater
    useEffect(() => {
        // subscribe to columnHeaderDragEnd which is much better than onColumnOrderChange
        return tableAPIRef.current.subscribeEvent(
            'columnHeaderDragEnd',
            params => {
                const cols = tableAPIRef.current.getAllColumns().reduce((a, c, i) => {
                    return { ...a, [c.variable]: i };
                }, []);

                updateProfile('anomalies', {
                    ...getProfile('anomalies'),
                    sort: cols,
                });
                setColumnsOrder(cols);
            }
        );
    }, [getProfile, tableAPIRef, updateProfile]);

    const getSeverity = (s) => {
        switch(true) {
            case s > 0.8:
                return (
                    <Tooltip title="Critical">
                        <div className={classes.severityChip} style={{ background: theme.HeatmapColors[0] }}>1</div>
                    </Tooltip>
                );
            case s > 0.6:
                return (
                    <Tooltip title="High">
                        <div className={classes.severityChip} style={{ background: theme.HeatmapColors[1] }}>2</div>
                    </Tooltip>
                );
            case s > 0.3:
                return (
                    <Tooltip title="Medium">
                        <div className={classes.severityChip} style={{ background: theme.HeatmapColors[2] }}>3</div>
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip title="Low">
                        <div className={classes.severityChip} style={{ background: theme.HeatmapColors[3] }}>4</div>
                    </Tooltip>
                );
        }
    };

    const headerRenderers = {
        data_source_1: () => {
            const theme = useTheme();
            return (
                <span>
                    Data Source 1 (<span style={{ color: theme.palette.error.light }}>Actual</span> : <span style={{ color: theme.palette.success.light }}>Expected</span>)
                </span>
            );
        },
        data_source_2: () => {
            const theme = useTheme();
            return (
                <span>
                    Data Source 2 (<span style={{ color: theme.palette.error.light }}>Actual</span> : <span style={{ color: theme.palette.success.light }}>Expected</span>)
                </span>
            );
        },
        data_source_3: () => {
            const theme = useTheme();
            return (
                <span>
                    Data Source 3 (<span style={{ color: theme.palette.error.light }}>Actual</span> : <span style={{ color: theme.palette.success.light }}>Expected</span>)
                </span>
            );
        },
    };
    const cellRenderers = {
        detector_id: (f, d) => <HighlightText text={`${RuleTitle(getRuleField(f.valueGetter(d), 'name'))}`} />,
        severity: (f, d) => getSeverity(_get(d, 'row._source.anomaly_grade', 0)),
        data_source: (f, d) => {
            const column = parseInt(d.field[d.field.length - 1]) - 1;
            const feature = _get(d, 'row._source.feature_data', []);
            const expected = _get(d, 'row._source.expected_values', []);
            
            if (column < feature.length) {
                const ev = _get(expected, '[0].value_list', []).find(v => v.feature_id === feature[column].feature_id);
                const evData = _get(ev, 'data', 0);
                return (
                    <Box display='inline'>
                        <Box display='inline'><HighlightText text={`${feature[column].feature_name}`} /></Box>:&nbsp;
                        <Box display='inline' style={{color: theme.palette.error.light}}><HighlightText text={`${ToFixed(feature[column].data, 2)}`} /></Box>&nbsp;:&nbsp;
                        <Box display='inline' style={{color: theme.palette.success.light}}><HighlightText text={`${ToFixed(evData, 2)}`} /></Box>
                    </Box>
                );
            }
            return (<Box></Box>);
        },
        entity: (f, d) => {
            const column = parseInt(d.field[d.field.length - 1]) - 1;
            const entity = _get(d, `row._source.entity[${column}]`, null);
            if (entity) {
                const eName = _get(entity, 'name', '');
                const eValue = _get(entity, 'value', '');
                if (eName.includes('orig_h') || eName.includes('resp_h')) {
                    return <IPAddress address={eValue} />;
                }
                return <HighlightText text={`${eName}: ${eValue}`} search={search} />
            }
            return <HighlightText text={""} />
        },
        float: (f, d) => <HighlightText text={`${ToFixed(f.valueGetter(d), 2)}`} />,
        date: (f, d) => <HighlightText text={`${Moment(f.valueGetter(d)).format(theme.date.long)}`} />,
        general: (f, d) => <HighlightText text={`${f.valueGetter(d)}`} />,
    };

    /************************ COLUMN SELECTOR SORT IS STILL BROKEN ************************/
    const updateColumns = useCallback((colsUpdate, reset) => {
        const profile = getProfile('anomalies');
        const f = OSAnomalyService.fields
        let cols = [];

        if (colsUpdate && colsUpdate.length) {
            cols = colsUpdate.map(x => ({
                ...x,
                width: columnWidth[x.variable] || x.width,
                renderCell: d => {
                    if (Object.prototype.hasOwnProperty.call(cellRenderers, x.variable)) {
                        return cellRenderers[x.variable](x, d);
                    } else if (x.variable === 'severity') {
                        return cellRenderers.severity(x, d);
                    } else if (x.variable.includes('entity')) {
                        return cellRenderers.entity(x, d);
                    } else if (x.variable.includes('data_source')) {
                        return cellRenderers.data_source(x, d);
                    } else if (x.type === 'number' && IsFloat(x.valueGetter(d))) {
                        return cellRenderers.float(x, d);
                    } else if (x.type === 'date') {
                        return cellRenderers.date(x, d);
                    } else {
                        cellRenderers.general(x, d);
                    }
                },
                renderHeader: headerRenderers[x.variable],
                hide: x.hide,
            }));
        } else if (reset || !_get(profile, 'columns', []).length) {
            cols = Object.keys(f).map(x => ({
                ...f[x],
                width: columnWidth[f[x].variable] || f[x].width,
                // valueGetter: f[x].getter || (d => _get(d.row, `${f[x].variable}`, `_source.${f[x].variable}`)),
                // renderCell: tableRenderers[f[x].variable],
                renderCell: d => {
                    if (Object.prototype.hasOwnProperty.call(cellRenderers, f[x].variable)) {
                        return cellRenderers[f[x].variable](f[x], d);
                    } else if (f[x].variable === 'severity') {
                        return cellRenderers.severity(f[x], d);
                    } else if (f[x].variable.includes('entity')) {
                        return cellRenderers.entity(f[x], d);
                    } else if (f[x].variable.includes('data_source')) {
                        return cellRenderers.data_source(f[x], d);
                    } else if (f[x].type === 'number' && IsFloat(f[x].valueGetter(d))) {
                        return cellRenderers.float(f[x], d);
                    } else if (f[x].type === 'date') {
                        return cellRenderers.date(f[x], d);
                    } else {
                        cellRenderers.general(f[x], d);
                    }
                },
                renderHeader: headerRenderers[f[x].variable],
                hide: f[x].hide,
            })).sort((a, b) => (a.order || 10000) - (b.order || 10000));
        } else {
            cols = Object.keys(f).map(x => ({
                ...f[x],
                width: columnWidth[f[x].variable] || f[x].width,
                // valueGetter: f[x].getter || (d => _get(d.row, `${f[x].variable}`, `_source.${f[x].variable}`)),
                // renderCell: tableRenderers[f[x].variable],
                renderCell: d => {
                    if (Object.prototype.hasOwnProperty.call(cellRenderers, f[x].variable)) {
                        return cellRenderers[f[x].variable](f[x], d);
                    } else if (f[x].variable === 'severity') {
                        return cellRenderers.severity(f[x], d);
                    } else if (f[x].variable.includes('entity')) {
                        return cellRenderers.entity(f[x], d);
                    } else if (f[x].variable.includes('data_source')) {
                        return cellRenderers.data_source(f[x], d);
                    } else if (f[x].type === 'number' && IsFloat(f[x].valueGetter(d))) {
                        return cellRenderers.float(f[x], d);
                    } else if (f[x].type === 'date') {
                        return cellRenderers.date(f[x], d);
                    } else {
                        cellRenderers.general(f[x], d);
                    }
                },
                renderHeader: headerRenderers[f[x].variable],
                hide: (_get(profile, 'columns', []).find(y => y.variable === f[x].variable) || {hide: true}).hide,
            })).sort((a, b) => (columnsOrder[a.variable] || 0) - (columnsOrder[b.variable] || 0));
        }
        const colsOrder = cols.reduce((a, c, i) => {
            return { ...a, [c.field]: i };
        }, []);
        updateProfile('anomalies', {
            ...profile,
            columns: cols,
            sort: colsOrder,
        });
        setColumns(cols);
        setColumnsOrder(colsOrder);
    }, [rules, data]);

    // columns updator
    useEffect(() => {
        if (!showColumnsSelector) {
            updateColumns();
        }
    }, [
        getProfile,
        // columnWidth,
        // columnsOrder,
        search,
        perms.write,
        perms.delete,
        data,
    ]);

    useEffect(() => {
        // check if timelinebar is visible...
        setTableHeight(height - 420);
    }, [height]);

    // useEffect(() => {
    //     if (rules && rules.length) {
    //         getData();
    //     }
    // }, [rules, selectedRule]);

    useEffect(() => {
        if (!init) {
            setInit(true);
        }
        if (rules && rules.length) {
            abortController.abort();
            getData();
        }
    }, [search, rules, selectedRule]);

    const getRuleField = (ruleID, field) => {
        const r = rules.find(x => x.uuid === ruleID);
        return _get(r, field, '');
    };

    const _handleSearchChange = ({ search }) => {
        setTimeout(() => {
            setAfter(undefined);
            setSearch(search || '');
        });
    };
    
    const handleSearchChange = _debounce(_handleSearchChange, 500);

    // provide a long debounce to give user a chance to type before failing for bad format
    const handleStartChange = useCallback(start => {
        if (!start || !start.isValid()) return;
        setTimeout(() => {
            setQuick();
            setAfter(undefined);
            setPaused(false);
            setData([]);
            setRange(current => [start ? start.utc().valueOf() : undefined, current[1]]);
        });
    }, []);

    const handleEndChange = useCallback(end => {
        if (end && !end.isValid()) return;
        setTimeout(() => {
            setQuick();
            setAfter(undefined);
            setPaused(false);
            setData([]);
            setRange(current => [current[0], end ? end.utc().valueOf() : new Date().valueOf()]);
        });
    }, []);

    // incoming from grid
    const handleOnSort = e => {
        if (!Object.prototype.toString.call(e).includes('Array')) {
            e = [e];
        }
        setTimeout(() => {
            setAfter(undefined);
            setPage(1);
            setPaused(false);
            tableAPIRef.current.scroll({ top: 0 });
    
            if (e.length && e[0].field === 'created') {
                setSort([{
                    field: 'execution_end_time',
                    sort: e[0].sort,
                }]);
            } else if (e.length && e[0].field.includes('data_source')) {
                setSort([{
                    field: 'feature_data.data',
                    sort: e[0].sort,
                }]);
            } else if (e.length && e[0].field.includes('severity')) {
                setSort([{
                    field: 'anomaly_grade',
                    sort: e[0].sort,
                }]);
            } else if (e.length && e[0].field.includes('entity')) {
                setSort([{
                    field: 'entity.value',
                    sort: e[0].sort,
                }]);
            } else if (!e.length) {
                setSort(DEFAULT_SORT);
            } else {
                setSort(e);
            }
        });
    };

    const handleViewAnomaly = (params) => {
        setPaused(true);
        setSelection(params.row);
    };

    const clearSelection = (prevSelection) => {
        setSelection(undefined);
    };

    const quickFilterTitle = useCallback(() => {
        const qf = QUICK_FILTERS.find(q => q.key === quick);
        return quick ? qf ? qf.title : 'Date Range' : 'Date Range';
    }, [quick]);

    const handleColumnResize = params => {
        const profile = getProfile('anomalies');
        updateProfile('anomalies', {
            ...profile,
            widths: { ...profile.widths || {}, [params.colDef.variable]: params.width },
        });

        setColumnWidth(current => ({
            ...current,
            [params.colDef.variable]: params.width,
        }));
    };

    const _handleColumnsReordered = (colData, _, table) => {
        const profile = getProfile('anomalies');

        const cols = table.api.getAllColumns();
        const colsOrder = cols.reduce((a, c, i) => {
            return { ...a, [c.field]: i };
        }, []);
        
        updateProfile('anomalies', {
            ...profile,
            columns: cols,
            sort: colsOrder,
        });
        setColumns(cols);
        setColumnsOrder(colsOrder);
    };
    
    const handleColumnsReordered = _debounce(_handleColumnsReordered, 500);
    
    // const handleColumnsUpdated = (cols) => {
    //     const colsOrder = cols.reduce((a, c, i) => {
    //         return { ...a, [c.variable]: i };
    //     }, []);

    //     const profile = getProfile('anomalies');
    //     updateProfile('anomalies', {
    //         ...profile,
    //         columns: cols,
    //         sort: colsOrder,
    //     });

    //     // setColumns(cols);
    //     setColumnsOrder(colsOrder);
    // };

    const handleOnQuickFilter = q => {
        setTimeout(() => {
            setQuick(q.key);
            setAfter(undefined);
            setPaused(false);
            setData([]);
            setRange([GetEpochDateFromQuick(q.key), new Date().valueOf()]);
        });
    };

    const getQueryParams = () => {
        const q = {
            limit: limit,
            sort: sort[0].field + ':' + sort[0].sort,
            from: range[0],
            to: range[1],
        };

        if (after) {
            q.after = after;
        }

        return q;
    };

    const getData = (pause) => {
        if (!loading && after !== 0) {
            // const severity = sev !== undefined ? sev : bySeverity;
            setLoading(true);
            if (search) {
                OSAnomalyService.searchAnomalies(search, getQueryParams()).then(res => {
                    if (!pause) {
                        setTimeout(() => {
                            setData(_get(res, 'anomalies.hits.hits', []));
                            setPaused(true);
                        });
                    } else {
                        setData(current => current.concat(_get(res, 'anomalies.hits.hits', [])));
                        if (_get(res, 'totalItems', 0) > limit || _has(res, 'continuationPointer')) {
                            setAfter(res.continuationPointer.replace('[', '').replace(']', ''));
                        } else {
                            setAfter(0);
                        }
                    }
                    setTotal(_get(res, 'totalItems', 0));
                    setLoading(false);
                }, err => {
                    toast.error('Unable to get search results for anomalies');
                    console.error(err);
                    setTotal(0);
                    setLoading(false);
                    setPaused(true);
                });
            } else if (bySeverity !== null && (selectedRule === 'all' || selectedRule === 'all_unfiltered')) {
                OSAnomalyService.getAllAnomaliesBySeverity(getQueryParams(), bySeverity, abortController.signal).then(res => {
                    if (!pause) {
                        setData(_get(res, 'anomalies.hits.hits', []));
                    } else {
                        setData(current => current.concat(_get(res, 'anomalies.hits.hits', [])));
                        if (_get(res, 'totalItems', 0) > limit || _has(res, 'continuationPointer')) {
                            setAfter(res.continuationPointer.replace('[', '').replace(']', ''));
                        } else {
                            setAfter(0);
                        }
                    }
                    setTotal(_get(res, 'totalItems', 0));
                    setLoading(false);
                }, err => {
                    console.error('Unable to get anomalies by severity ::: ', err);
                    toast.error('Unable to get anomalies by severity');
                    setTotal(0);
                    setLoading(false);
                    setPaused(true);
                });
            } else if (bySeverity !== null && (selectedRule !== 'all' || selectedRule !== 'all_unfiltered')) {
                const r = rules.find(x => x.name === selectedRule);
                OSAnomalyService.getAnomaliesBySeverity(r.uuid, getQueryParams(), bySeverity, abortController.signal).then(res => {
                    if (!pause) {
                        setData(_get(res, 'anomalies.hits.hits', []));
                    } else {
                        setData(current => current.concat(_get(res, 'anomalies.hits.hits', [])));
                        if (_get(res, 'totalItems', 0) > limit || _has(res, 'continuationPointer')) {
                            setAfter(res.continuationPointer.replace('[', '').replace(']', ''));
                        } else {
                            setAfter(0);
                        }
                    }
                    setTotal(_get(res, 'totalItems', 0));
                    setLoading(false);
                }, err => {
                    toast.error('Unable to get anomalies');
                    console.error(err);
                    setTotal(0);
                    setLoading(false);
                    setPaused(true);
                });
            } else if (selectedRule === 'all' || selectedRule === 'all_unfiltered') {
                OSAnomalyService.getAllAnomalies(selectedRule.includes('unfiltered') ? false : true, getQueryParams(), abortController.signal).then(res => {
                    if (!pause) {
                        setData(_get(res, 'anomalies.hits.hits', []));
                    } else {
                        setData(current => current.concat(_get(res, 'anomalies.hits.hits', [])));
                        if (_get(res, 'totalItems', 0) > limit || _has(res, 'continuationPointer')) {
                            setAfter(res.continuationPointer.replace('[', '').replace(']', ''));
                        } else {
                            setAfter(0);
                        }
                    }
                    setTotal(_get(res, 'totalItems', 0));
                    setLoading(false);
                }, err => {
                    toast.error('Unable to get anomalies: ' + err);
                    console.error(err);
                    setTotal(0);
                    setLoading(false);
                    setPaused(true);
                });
            } else {
                const r = rules.find(x => x.name === selectedRule);
                OSAnomalyService.getAnomalies(r.uuid, getQueryParams(), abortController.signal).then(res => {
                    if (!pause) {
                        setData(_get(res, 'anomalies.hits.hits', []));
                    } else {
                        setData(current => current.concat(_get(res, 'anomalies.hits.hits', [])));
                        if (_get(res, 'totalItems', 0) > limit || _has(res, 'continuationPointer')) {
                            setAfter(res.continuationPointer.replace('[', '').replace(']', ''));
                        } else {
                            setAfter(0);
                        }
                    }
                    setTotal(_get(res, 'totalItems', 0));
                    setLoading(false);
                }, err => {
                    toast.error('Unable to get anomalies');
                    console.error(err);
                    setTotal(0);
                    setLoading(false);
                    setPaused(true);
                });
            }
        }
    };

    const handleSelectRule = (e) => {
        tableAPIRef.current.scroll({ top: 0 });
        setAfter(undefined);
        setSelectedRule(e.target.value);
    };

    const handleShowColumns = () => {
        setShowColumnsSelector(true);
    };

    const handleScrollEnd = params => {
        if (params.virtualRowsCount > params.viewportPageSize) {
            setPage(p => ++p);
        }
    };

    const handlePlayPause = () => {
        if (paused) {
            tableAPIRef.current.scroll({ top: 0 });
            setTimeout(() => {
                setAfter(undefined);
                setPage(1);
                setPaused(!paused);
            });
        } else {
            setPaused(!paused);
        }
    };

    const handleResetPage = () => {
        setTimeout(() => {
            handleOnQuickFilter({ key: QUICK_HOUR });
            handleSelectRule({ target: { value: 'all' } });
            setSort(DEFAULT_SORT);
            setSearch('');
            setAfter(undefined);
            setBySeverity(null);
            setPaused(false);
            setPage(1);
            updateColumns(null, true);
        });
        tableAPIRef.current.setSortModel(DEFAULT_SORT);
    };

    const handleHeatmapClick = (e, d) => {
        abortController.abort();
        tableAPIRef.current.scroll({ top: 0 });

        let sev;
        if (typeof e.target.__data__ === 'number') {
            sev = e.target.__data__;
        } else if (!Object.prototype.toString.call(e.target.__data__).includes('Date')) {
            sev= e.target.__data__.series;
        } else {
            return;
        }

        if (bySeverity === sev) {
            setTimeout(() => {
                setAfter(undefined);
                setPaused(false);
                setBySeverity(null)
            });
        } else {
            setTimeout(() => {
                setAfter(undefined);
                setPaused(false);
                setBySeverity(sev);
                setData([]);
            });
        }
    };

    return (
        <React.Fragment>
            <main className={clsx(classes.content)}>
                <Card style={{ padding: 10 }}>
                    {/* time selection panel */}
                    <Box style={{ right: 10, top: -35 }} mr={4} ml={4}>
                        <Grid container style={{ paddingTop: 0 }} className={classes.nowrap} direction="row" justifyContent="flex-end" alignItems="flex-end" wrap="nowrap">
                            <Grid container spacing={2} direction="row" justifyContent="flex-end" alignItems="flex-end" wrap="nowrap">
                                <Grid item size="grow" style={{ width: '100%' }}>
                                    Anomalies by Time and Severity
                                </Grid>
                                <Grid item>
                                    {/* quick filters */}
                                    <PopupMenu
                                        tooltip="select range"
                                        className={classes.stealRoom}
                                        items={QUICK_FILTERS}
                                        itemkey="key"
                                        onSelect={handleOnQuickFilter}
                                        selection={quick}
                                        title={quickFilterTitle()}
                                        name="quick filters"/>
                                </Grid>
                                <Grid item className={classes.datePicker}>
                                    <DateTimePicker
                                        label="Start"
                                        aria-label="start"
                                        value={range[0]}
                                        onChange={handleStartChange}
                                        inputFormat={theme.date.short}
                                        disableMaskedInput={true}
                                        maxDate={range[1]}
                                        showTodayButton={true}
                                        showToolbar={true}
                                        ampm={false}/>
                                </Grid>
                                <Grid item className={classes.datePicker}>
                                    <DateTimePicker
                                        clearable={true}
                                        ampm={false}
                                        aria-label="end"
                                        label="End"
                                        value={range[1]}
                                        onChange={handleEndChange}
                                        inputFormat={theme.date.short}
                                        disableMaskedInput={true}
                                        minDate={range[0]}
                                        showTodayButton={true}
                                        showToolbar={true}/>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box>
                        <Heatmap
                            data={heatmapData}
                            height={HEATMAP_HEIGHT}
                            width={width - 200}
                            start={range[0]}
                            end={range[1]}
                            ruleID={selectedRule.includes('all') ? selectedRule : rules.find(x => x.name === selectedRule).uuid}
                            severity={bySeverity}
                            clickHandler={handleHeatmapClick}
                        />
                    </Box>
                </Card>
            </main>
            <div ref={toolbarRef}/>
            <Grid spacing={1}
                container
                direction="row"
                className={classes.fullWidth}
                style={{ marginTop: 10 }}
                mb={3}
                display="flex"
                justify="space-between"
                alignItems="baseline"
            >
                <Grid xs={4} item>
                    <SearchBar immediate
                        value={search}
                        style={{ width: '100%' }}
                        onSearch={handleSearchChange}
                        searchservice={OSAnomalyService}
                        reset={handleResetPage}
                        clearSearchOnReset />
                </Grid>
                <Grid xs={8} container item justify="space-between" alignItems="baseline">
                    <Box display="flex" style={{ flexGrow: 1 }}>
                        <FormControl fullWidth className={classes.select}>
                            <InputLabel id="rule-select-label">Anomaly Detection Rule</InputLabel>
                            <Select
                                labelId="rule-select-label"
                                id="rule-select"
                                fullWidth
                                input={<Input id="rule-select-input" />}
                                onChange={handleSelectRule}
                                value={selectedRule}>
                                    <MenuItem key="all" value={'all'}>all</MenuItem>
                                    <MenuItem key="all_unfiltered" value={'all_unfiltered'}>all (unfiltered)</MenuItem>
                                    {rules.map(x => {
                                        return <MenuItem key={x.name} value={x.name}>{x.name}</MenuItem>
                                    })}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box ml={2}>
                        <Button variant="contained" color="primary" onClick={handleShowColumns}><ViewWeekIcon /> Columns</Button>
                        <ColumnSelector
                            show={showColumnsSelector}
                            columns={columns}
                            sortCol={sort[0].field}
                            sortOrder={sort[0].sort}
                            setSort={handleOnSort}
                            anchorEl={toolbarRef.current}
                            onClickAway={() => setShowColumnsSelector(false)}
                            onOK={updateColumns}
                        />
                    </Box>
                    <Box ml={2}>
                        <Button variant="contained" color="primary" onClick={handlePlayPause}>
                            {!paused ? <div style={{display: 'flex'}}><PauseIcon /> Pause</div> : <div style={{display: 'flex'}}><PlayIcon /> Fetch</div>}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12}
                className={clsx(classes.table)}
                style={{ height: tableHeight }}>
                <DataGridPro
                    components={{
                        LoadingOverlay,
                        NoRowsOverlay,
                        // Footer: TableFooter,
                        // Header: TableToolbar,
                    }}
                    apiRef={tableAPIRef}
                    loading={loading === true}
                    columns={columns}
                    rows={data}
                    columnBuffer={5}
                    rowBuffer={250}
                    rowHeight={38}
                    sortingMode='server'
                    sortModel={sort}
                    // sortingOrder={['desc', 'asc']}
                    onSortModelChange={handleOnSort}
                    rowCount={total}
                    onError={e => console.error(e)}
                    disableMultipleColumnsSorting={true}
                    onRowClick={handleViewAnomaly}
                    disableColumnSelector={true}
                    disableColumnMenu={true}
                    onColumnResize={handleColumnResize}
                    onColumnOrderChange={handleColumnsReordered}
                    onRowsScrollEnd={handleScrollEnd}
                    getRowId={r => r._id}
                    disableVirtualization
                />
            </Grid>

            <Drawer
                sx={{
                    width: Math.min(600, 2 * width / 2),
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: Math.min(600, 2 * width / 2),
                        boxSizing: 'border-box',
                    },
                }}
                open={!!selection}
                anchor="right"
                variant="persistent">
                {selection && <AnomalyDetail
                    rules={rules}
                    width={Math.min(600, 2 * width / 2)}
                    anom={selection}
                    onClose={() => {clearSelection(selection)}} />
                }
            </Drawer>
        </React.Fragment>
    );
};

export default Anomalies;
